import {
  json,
  nullableString,
  request,
  requireArray,
  requireString,
} from '@/lib/api'
import type { components } from '@/lib/openapi.generated'

export const ASSISTANT_TIERS = ['standard', 'professional', 'ultra'] as const

export type AssistantTier = (typeof ASSISTANT_TIERS)[number]

export const ASSISTANT_TIER_LABELS: Record<AssistantTier, string> = {
  standard: 'Standard',
  professional: 'Professional',
  ultra: 'Ultra',
}

export type AssistantTierConfig = {
  enabledTiers: AssistantTier[]
  defaultTier: AssistantTier
  sourceEntityId: string | null
}

export type PutAssistantTierConfigRequest =
  components['schemas']['PutAssistantTierConfigRequest']

function requireTier(value: unknown, field: string): AssistantTier {
  const tier = requireString(value, field)
  if (!(ASSISTANT_TIERS as readonly string[]).includes(tier))
    throw new Error(`${field} is not a known assistant tier: ${tier}`)
  return tier as AssistantTier
}

function decodeAssistantTierConfig(raw: unknown): AssistantTierConfig {
  const config = raw as Record<string, unknown>
  return {
    enabledTiers: requireArray(config.enabledTiers, 'enabledTiers', (tier) =>
      requireTier(tier, 'enabledTiers'),
    ),
    defaultTier: requireTier(config.defaultTier, 'defaultTier'),
    sourceEntityId: nullableString(config.sourceEntityId, 'sourceEntityId'),
  }
}

const tiersPath = (entityId: string) =>
  `/api/v1/entities/${encodeURIComponent(entityId)}/assistant-tiers`

export const getAssistantTierConfig = (entityId: string) =>
  request(tiersPath(entityId), decodeAssistantTierConfig)

export const putAssistantTierConfig = (
  entityId: string,
  body: PutAssistantTierConfigRequest,
) => request(tiersPath(entityId), decodeAssistantTierConfig, json('PUT', body))
